import { baseApi } from "@/services/api";
import type {
  BrandArchivePreflight,
  BrandEligibleMembership,
  BrandLifecycleView,
  BrandPermanentDeletePreflight,
} from "./brandLifecycle";

export interface BrandRow {
  brandId: string;
  id: string;
  name: string;
  brandName: string;
  slug: string;
  industry: string | null;
  description: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  locationCount: number;
  locations: number;
  zones: number;
  logoKey?: string | null;
  logoUrl?: string | null;
  /** Soft-delete archive fields (present when view=archived). */
  archived?: boolean;
  deletedAt?: string | null;
  deletedBy?: string | null;
  deletedReason?: string | null;
  restoredAt?: string | null;
  restoredBy?: string | null;
}

export interface BrandLogoUploadUrlResult {
  uploadUrl: string;
  key: string;
  logoKey: string;
  bucket: string;
  expiresIn: number;
}

/** Canonical Brand Location fleet row (GET /sa/brands/{id}/locations). */
export interface BrandLocationRow {
  locationId: string;
  locationName: string;
  brandId: string;
  status: string | null;
  address: string | null;
  address_street?: string | null;
  address_city?: string | null;
  address_state?: string | null;
  address_zipCode?: string | null;
  address_country?: string | null;
  timezone?: string | null;
  storeNumber?: string | null;
  currentOperatorMasterAccountId?: string | null;
  currentOperatorBusinessName?: string | null;
  assignmentStatus?: string | null;
  operatorAnomaly?: {
    code: string;
    count: number;
    message: string;
  } | null;
}

export type {
  BrandArchivePreflight,
  BrandEligibleMembership,
  BrandLifecycleView,
  BrandPermanentDeletePreflight,
};

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  message?: string;
}

function unwrap<T>(raw: ApiEnvelope<T>, fallbackMessage: string): T {
  if (!raw || raw.success !== true || !raw.data) {
    throw new Error(raw?.message || fallbackMessage);
  }
  return raw.data;
}

export const brandsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listBrands: builder.query<
      { brands: BrandRow[]; view: BrandLifecycleView },
      { view?: BrandLifecycleView } | void
    >({
      query: (args) => {
        const view = args && "view" in args && args.view ? args.view : "active";
        return {
          url: "/sa/brands",
          method: "GET",
          params: { view },
        };
      },
      transformResponse: (
        raw: ApiEnvelope<{ brands: BrandRow[]; view?: BrandLifecycleView }>,
      ) => {
        const data = unwrap(raw, "Failed to load brands");
        return {
          brands: data.brands || [],
          view: data.view === "archived" ? "archived" : "active",
        };
      },
      providesTags: ["Accounts"],
    }),
    createBrand: builder.mutation<
      { brand: BrandRow },
      {
        name: string;
        industry?: string;
        description?: string;
        status?: string;
      }
    >({
      query: (body) => ({
        url: "/sa/brands",
        method: "POST",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<{ brand: BrandRow }>) =>
        unwrap(raw, "Failed to create brand"),
      invalidatesTags: ["Accounts"],
    }),
    updateBrand: builder.mutation<
      { brand: BrandRow },
      {
        brandId: string;
        name?: string;
        industry?: string;
        description?: string;
        status?: string;
        logoKey?: string | null;
      }
    >({
      query: ({ brandId, ...body }) => ({
        url: `/sa/brands/${encodeURIComponent(brandId)}`,
        method: "PUT",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<{ brand: BrandRow }>) =>
        unwrap(raw, "Failed to update brand"),
      invalidatesTags: ["Accounts"],
    }),
    getBrandLogoUploadUrl: builder.mutation<
      BrandLogoUploadUrlResult,
      { brandId: string; contentType: string }
    >({
      query: ({ brandId, contentType }) => ({
        url: `/sa/brands/${encodeURIComponent(brandId)}/logo-upload-url`,
        method: "POST",
        body: { contentType },
      }),
      transformResponse: (raw: ApiEnvelope<BrandLogoUploadUrlResult>) =>
        unwrap(raw, "Failed to get brand logo upload URL"),
    }),
    archiveBrand: builder.mutation<
      | { preflight: BrandArchivePreflight }
      | {
          brand: BrandRow;
          alreadyArchived?: boolean;
          revokedMembershipIds?: string[];
          impact?: {
            affectedClientCount?: number;
            liveMembershipCount?: number;
            locationCountPreserved?: number;
            zoneCountPreserved?: number | null;
            revokedMasterAccountIds?: string[];
          } | null;
          correlationId?: string | null;
        },
      {
        brandId: string;
        nameConfirmation?: string;
        reason?: string;
        dryRun?: boolean;
      }
    >({
      query: ({ brandId, nameConfirmation, reason, dryRun }) => ({
        url: `/sa/brands/${encodeURIComponent(brandId)}/archive`,
        method: "POST",
        body: {
          ...(nameConfirmation != null ? { nameConfirmation } : {}),
          ...(reason != null ? { reason } : {}),
          ...(dryRun ? { dryRun: true } : {}),
        },
      }),
      transformResponse: (
        raw: ApiEnvelope<
          | { preflight: BrandArchivePreflight }
          | {
              brand: BrandRow;
              alreadyArchived?: boolean;
              revokedMembershipIds?: string[];
              impact?: {
                affectedClientCount?: number;
                liveMembershipCount?: number;
                locationCountPreserved?: number;
                zoneCountPreserved?: number | null;
                revokedMasterAccountIds?: string[];
              } | null;
              correlationId?: string | null;
            }
        >,
      ) => unwrap(raw, "Failed to archive brand"),
      invalidatesTags: (result, error, arg) =>
        arg.dryRun ? [] : ["Accounts"],
    }),
    getBrandRestoreEligibility: builder.query<
      { brand: BrandRow; eligibleMemberships: BrandEligibleMembership[] },
      string
    >({
      query: (brandId) => ({
        url: `/sa/brands/${encodeURIComponent(brandId)}/restore`,
        method: "GET",
      }),
      transformResponse: (
        raw: ApiEnvelope<{
          brand: BrandRow;
          eligibleMemberships: BrandEligibleMembership[];
        }>,
      ) => {
        const data = unwrap(raw, "Failed to load restore eligibility");
        return {
          brand: data.brand,
          eligibleMemberships: data.eligibleMemberships || [],
        };
      },
    }),
    restoreBrand: builder.mutation<
      {
        brand: BrandRow;
        restoredMembershipIds?: string[];
        eligibleMembershipIds?: string[];
      },
      { brandId: string; membershipIds: string[]; notes?: string }
    >({
      query: ({ brandId, membershipIds, notes }) => ({
        url: `/sa/brands/${encodeURIComponent(brandId)}/restore`,
        method: "POST",
        body: {
          membershipIds,
          ...(notes != null && notes !== "" ? { notes } : {}),
        },
      }),
      transformResponse: (
        raw: ApiEnvelope<{
          brand: BrandRow;
          restoredMembershipIds?: string[];
          eligibleMembershipIds?: string[];
        }>,
      ) => unwrap(raw, "Failed to restore brand"),
      invalidatesTags: ["Accounts"],
    }),
    permanentDeleteBrand: builder.mutation<
      | { preflight: BrandPermanentDeletePreflight }
      | {
          brandId: string;
          alreadyDeleted?: boolean;
          name?: string | null;
          logoCleanup?: { deleted: number; skipped: boolean } | null;
        },
      {
        brandId: string;
        nameConfirmation: string;
        confirm: "DELETE";
        reason: string;
        dryRun?: boolean;
      }
    >({
      query: ({ brandId, nameConfirmation, confirm, reason, dryRun }) => ({
        url: `/sa/brands/${encodeURIComponent(brandId)}/permanent-delete`,
        method: "POST",
        body: {
          nameConfirmation,
          confirm,
          reason,
          ...(dryRun === true ? { dryRun: true } : {}),
        },
      }),
      transformResponse: (
        raw: ApiEnvelope<
          | { preflight: BrandPermanentDeletePreflight }
          | {
              brandId: string;
              alreadyDeleted?: boolean;
              name?: string | null;
              logoCleanup?: { deleted: number; skipped: boolean } | null;
            }
        >,
      ) => unwrap(raw, "Failed to permanently delete brand"),
      invalidatesTags: (_result, _error, arg) =>
        arg.dryRun === true ? [] : ["Accounts"],
    }),
    listBrandLocations: builder.query<
      {
        brandId: string;
        brandName: string | null;
        locations: BrandLocationRow[];
        locationCount: number;
        ownershipNote?: string;
      },
      string
    >({
      query: (brandId) => ({
        url: `/sa/brands/${encodeURIComponent(brandId)}/locations`,
        method: "GET",
      }),
      transformResponse: (
        raw: ApiEnvelope<{
          brandId: string;
          brandName?: string | null;
          locations: BrandLocationRow[];
          locationCount?: number;
          ownershipNote?: string;
        }>,
      ) => {
        const data = unwrap(raw, "Failed to load brand locations");
        const locations = data.locations || [];
        return {
          brandId: data.brandId,
          brandName: data.brandName ?? null,
          locations,
          locationCount:
            typeof data.locationCount === "number"
              ? data.locationCount
              : locations.length,
          ownershipNote: data.ownershipNote,
        };
      },
      providesTags: (_r, _e, id) => [
        { type: "Accounts", id: `brand-locs-${id}` },
      ],
    }),
  }),
});

export const {
  useListBrandsQuery,
  useCreateBrandMutation,
  useUpdateBrandMutation,
  useGetBrandLogoUploadUrlMutation,
  useArchiveBrandMutation,
  useGetBrandRestoreEligibilityQuery,
  useLazyGetBrandRestoreEligibilityQuery,
  useRestoreBrandMutation,
  usePermanentDeleteBrandMutation,
  useListBrandLocationsQuery,
} = brandsApi;
